import { useAtomValue } from 'jotai';
import { CreateAreaMapLayers } from 'components/create-area-panel/create-area-map-layers';
import { CreateAreaMapOverlays } from 'components/create-area-panel/create-area-map-overlays';
import {
  useAddVertexPoint,
  useMapWorkspaceState,
} from 'components/create-area-panel/hooks';
import { workspaceDrawShapeAtom } from 'components/create-area-panel/atoms';
import { mapSearchRefAtom, SUPPRESS_BOUNDS_SEARCH } from '../atoms';
import { createAreaOpenAtom } from './atoms';

/**
 * The open panel's layers, subscribed to its state only while it is open.
 *
 * @return {JSX.Element} The layers.
 */
function OpenCreateAreaLayers(): JSX.Element {
  const { drawing } = useMapWorkspaceState();
  const drawShape = useAtomValue(workspaceDrawShapeAtom);

  return (
    <CreateAreaMapLayers
      drawShape={drawShape}
      drawnPolygons={drawing.polygons}
      onDrawnPolygonsChange={drawing.onPolygonsChange}
      drawing={drawing.isDrawing}
      onDrawingChange={drawing.onIsDrawingChange}
      editingIndex={drawing.editingIndex}
      onEditingIndexChange={drawing.onEditingIndexChange}
      previewGeometry={drawing.previewGeometry}
      previewColor={drawing.previewColor}
      onUnitClick={drawing.onUnitClick}
      onSurveyAreaPick={drawing.onSurveyAreaPick}
      pickedSurveyAreas={drawing.pickedSurveyAreas}
      snapToGrid={drawing.snapToGrid}
      fitEventData={SUPPRESS_BOUNDS_SEARCH}
    />
  );
}

/**
 * The panel's shapes on Map Query's map — survey picks, drawn polygons, the
 * derived preview. Mount inside `<Map>`, after the dataset layers.
 *
 * @return {JSX.Element | null} The layers while the panel is open.
 */
export function MapQueryCreateAreaLayers(): JSX.Element | null {
  const open = useAtomValue(createAreaOpenAtom);
  return open ? <OpenCreateAreaLayers /> : null;
}

/**
 * The open panel's overlays, subscribed to its state only while it is open.
 *
 * @return {JSX.Element} The overlays.
 */
function OpenCreateAreaOverlays(): JSX.Element {
  const mapRef = useAtomValue(mapSearchRefAtom);
  const { drawing } = useMapWorkspaceState();
  const addVertexPoint = useAddVertexPoint(mapRef);

  return (
    <CreateAreaMapOverlays
      mapRef={mapRef}
      editingIndex={drawing.editingIndex}
      addVertexPoint={addVertexPoint}
      surveyPicking={!!drawing.onSurveyAreaPick}
      snapToGrid={drawing.snapToGrid}
      workspace={drawing.needsMap}
    />
  );
}

/**
 * The panel's notes over Map Query's map: the edit banner, the add-vertex tip
 * and the zoom hints. Mount in the map's positioned container, outside `<Map>`.
 *
 * @return {JSX.Element | null} The overlays while the panel is open.
 */
export function MapQueryCreateAreaOverlays(): JSX.Element | null {
  const open = useAtomValue(createAreaOpenAtom);
  return open ? <OpenCreateAreaOverlays /> : null;
}
